/* eslint-disable no-console */
/**
 * @fileoverview Componente Organism ApiTemplateMessage para gestión de plantillas de API de WhatsApp con arquitectura de hooks por capas.
 * Implementa separación estricta de responsabilidades mediante hooks especializados (Data/Form/Actions/UI) y composición de componentes globales.
 * Optimizado con React.memo, useCallback y useMemo para performance y mantenibilidad.
 *
 * @module ApiTemplateMessage
 * @category Component - Organism
 * @since 1.0.0
 * @version 3.2.0 - Documentación JSDoc completa, optimización y descripción de arquitectura.
 */
import React, { memo, useCallback, useRef } from "react";
import { useTranslation } from 'react-i18next';
import FormProviderMC from '../../components/FormProviderMC';
import InputMC from '../../components/InputMC';
import DatePickerMC from '../../components/DatePickerMC';
import TeamSelect from '../../components/TeamSelect';
import WsTemplateTypeSelect from '../../components/WsTemplateTypeSelect';
import TemplateSelector from './components/TemplateSelector';
import TemplateMessagePreview from './components/TemplateMessagePreview';
import DropzoneFileUploader from "../../components/DropzoneFileUploader";

// Hooks especializados por capa
import useApiTemplateData from '../../hooks/api-template-message/data/useApiTemplateData';
import useApiTemplateForm from '../../hooks/api-template-message/forms/useApiTemplateForm';
import useApiTemplateActions from '../../hooks/api-template-message/actions/useApiTemplateActions';
import useApiTemplateUI from '../../hooks/api-template-message/ui/useApiTemplateUI';
import useNewChatMsIntegration from '../../hooks/api-template-message/data/useNewChatMsIntegration';
import DropdownMC from '../../components/DropdownMC';
import { integrationLabel } from 'services/inbox/resolveNewChatMsIntegration';
import { useTraceUpdate } from '../../hooks/shared/useDebugRenders';
import CustomerInfo from "./components/CustomerInfo";
import { sharingInformationService } from "../../../services/sharingInformationService";
import TemplateTypeCarousel from "./components/TemplateTypeCarousel";
import useTemplateMessageData from "../../hooks/newchat/data/useTemplateMessageData";

const ApiTemplateMessage = memo(({
  closeModal,
  currentCustomer,
  type,
  hasCustomer,
  onOutboundSendingChange,
  msContext
}) => {
  const { t } = useTranslation('NewChat');
  const sendInFlightRef = useRef(false);
  const fallbackMs = useNewChatMsIntegration(currentCustomer, { enabled: !msContext });
  const {
    isMsWhatsapp,
    showSelector,
    integrations,
    selectedIntegration,
    selectIntegration,
    chatId,
    loading: integrationsLoading
  } = msContext || fallbackMs;

  // Hook de debugging para rastrear renders y cambios de props
  useTraceUpdate('ApiTemplateMessage', {
    closeModal,
    currentCustomer,
    type,
    hasCustomer
  }, {
    logRenderCount: false,
    logPropChanges: false,
    logStackTrace: false
  });

  // UI Layer: Estados de interfaz y selecciones de usuario
  const {
    selectedTeam,
    selectedTemplateType,
    selectedTemplate,
    handleTeamSelect,
    handleTemplateTypeSelect,
    handleIntegrationSelect,
    handleTemplateChange
  } = useApiTemplateUI();

  // Data Layer: Configuración de archivos y procesamiento de variables dinámicas
  const {
    fileConfig,
    dynamicFieldsData
  } = useApiTemplateData(selectedTemplate, t);

  /**
   * LAYER 1: DATA LAYER
   * Responsabilidad: Gestión pura de datos de plantilla
   */
  const {
    dynamicFieldNames,
    carouselData
  } = useTemplateMessageData(selectedTemplate, dynamicFieldsData);

  // Form Layer: Estado de formulario y validación
  const {
    methods,
    resetFormWithDefaults
  } = useApiTemplateForm({
    selectedTemplate,
    fileConfig,
    type,
    t,
    dynamicFieldsData
  });

  // Actions Layer: Operaciones de negocio
  const {
    sendTemplateMessage,
    sendReminderMessage
  } = useApiTemplateActions(currentCustomer, selectedTemplate, t, dynamicFieldsData, {
    isMsWhatsapp,
    integration: selectedIntegration,
    chatId
  });

  const { handleSubmit } = methods;

  /**
   * Callback para seleccionar equipo y resetear formulario.
   * @function handleTeamSelectWithReset
   * @memberof ApiTemplateMessage
   * @param {string|number} teamId - ID del equipo seleccionado
   */
  const handleTeamSelectWithReset = useCallback((teamId) => {
    handleTeamSelect(teamId, resetFormWithDefaults);
  }, [handleTeamSelect, resetFormWithDefaults]);

  /**
   * Callback para seleccionar tipo de plantilla y resetear formulario.
   * @function handleTemplateTypeSelectWithReset
   * @memberof ApiTemplateMessage
   * @param {string|number} typeId - ID del tipo de plantilla seleccionado
   */
  const handleTemplateTypeSelectWithReset = useCallback((typeId) => {
    handleTemplateTypeSelect(typeId, resetFormWithDefaults);
  }, [handleTemplateTypeSelect, resetFormWithDefaults]);

  const handleIntegrationSelectWithReset = useCallback((integrationId) => {
    handleIntegrationSelect(integrationId, resetFormWithDefaults);
    selectIntegration(integrationId);
  }, [handleIntegrationSelect, resetFormWithDefaults, selectIntegration]);

  /**
   * Callback para cambiar plantilla y limpiar campos/archivos.
   * @function handleTemplateChangeWithCleanup
   * @memberof ApiTemplateMessage
   * @param {Object} template - Plantilla seleccionada
   */
  const handleTemplateChangeWithCleanup = useCallback((template) => {
    handleTemplateChange(template, () => {
      if (template) {
        // Limpiar archivo principal cuando cambie la plantilla
        methods.setValue('attachedFile', []);

        // Limpiar archivos de carousels si existen
        if (dynamicFieldsData?.cards) {
          dynamicFieldsData.cards.forEach((_, index) => {
            methods.setValue(`carouselFile_${index}`, []);
          });
        }

        // Limpiar campos dinámicos existentes
        dynamicFieldsData.main?.variables?.forEach((field) => {
          methods.setValue(field.fieldName, '');
        });

        // Limpiar campos dinámicos de cards y botones si existen
        dynamicFieldsData.cards?.forEach((carousel) => {
          carousel.variables.forEach((field) => {
            methods.setValue(field.fieldName, '');
          });
        });
      }
    });
  }, [handleTemplateChange, methods, dynamicFieldsData]);

  /**
   * Renderiza los inputs para variables dinámicas de la plantilla.
   */
  const renderInputVariables = useCallback((variables) => variables.map(
    (field) => (
      <div key={`variable-${field.originalId}-${field.variableNumber}`}>
        <InputMC
          id={`variable-input-${field.originalId}-${field.variableNumber}`}
          name={field.fieldName}
          label={`${t('apiMessage.value')} {{${field.originalId}}}`}
          placeholder={t('apiMessage.valuePlaceholder', { value: field.originalId })}
          useFloatingLabel
          required
        />
      </div>
    )
  ), [t]);

  /**
   * Callback para manejar el submit del formulario y enviar el mensaje.
   */
  const handleFormSubmit = useCallback(
    async (data) => {
      if (sendInFlightRef.current) return;
      sendInFlightRef.current = true;
      onOutboundSendingChange?.(true);

      const operationCallbacks = {
        onSuccess: () => {
          console.log('onSuccess en handleFormSubmit', type);
          if (type === 'reminder') {
            console.log('setSubject en handleFormSubmit', type);
            sharingInformationService.setSubject({
              refreshListDataReminder: true
            });
            sharingInformationService.setSubject({});
          }
          closeModal?.();
        },
        onError: (error) => {
          console.error('Error en handleFormSubmit:', error);
        }
      };

      try {
        if (type === 'reminder') {
          await sendReminderMessage(data, operationCallbacks);
        } else {
          await sendTemplateMessage(data, operationCallbacks);
        }
      } finally {
        sendInFlightRef.current = false;
        onOutboundSendingChange?.(false);
      }
    },
    [
      type,
      sendReminderMessage,
      sendTemplateMessage,
      closeModal,
      onOutboundSendingChange
    ]
  );

  return (
    <div className="tw-wrapper">
      <FormProviderMC
        id="gs-message-form"
        methods={methods}
        onSubmit={handleSubmit(handleFormSubmit)}
        className="tw-flex tw-flex-col tw-h-full tw-space-y-4"
      >
        <CustomerInfo currentCustomer={currentCustomer} />

        <div className="tw-flex-1 tw-space-y-4">
          {showSelector && (
            <DropdownMC
              key={selectedIntegration?.id || 'empty-integration'}
              name="platformRetailerId"
              id="platformRetailerId"
              label={t('apiMessage.selectIntegration')}
              placeholder={t('apiMessage.selectIntegrationPlaceholder')}
              options={integrations.map((integration) => ({
                id: integration.id,
                label: integrationLabel(integration)
              }))}
              defaultValue={selectedIntegration?.id || ''}
              onSelect={handleIntegrationSelectWithReset}
              useFloatingLabel
              search
              disabled={integrationsLoading || integrations.length === 0}
            />
          )}

          <div>
            <TeamSelect
              label={t('apiMessage.selectTeam')}
              placeholder={t('apiMessage.selectTeamPlaceholder')}
              defaultValue={selectedTeam}
              onSelect={handleTeamSelectWithReset}
              useFloatingLabel
              className="tw-w-full"
            />
          </div>

          <div>
            <WsTemplateTypeSelect
              label={t('apiMessage.templateType')}
              placeholder={t('apiMessage.allTypesPlaceholder')}
              defaultValue={selectedTemplateType}
              onSelect={handleTemplateTypeSelectWithReset}
              useFloatingLabel
              className="tw-w-full"
            />
          </div>

          <TemplateSelector
            name="selectedTemplate"
            label={t('apiMessage.selectPlaceholder')}
            currentCustomer={currentCustomer}
            selectedTeam={selectedTeam}
            selectedTemplateType={selectedTemplateType}
            placeholder={t('apiMessage.selectTemplate')}
            debounceTime={300}
            platformRetailerId={isMsWhatsapp ? selectedIntegration?.id : null}
            requirePlatformRetailerId={isMsWhatsapp}
            onSelect={handleTemplateChangeWithCleanup}
          />

          {selectedTemplate && dynamicFieldsData?.main?.variables?.length > 0 && (
            <div className="tw-space-y-4">
              <h4 className="tw-text-base tw-font-medium tw-text-gray-900">
                {t('apiMessage.variableValue')}
              </h4>
              {renderInputVariables(dynamicFieldsData.main.variables)}
            </div>
          )}

          {type === 'reminder' && (
            <DatePickerMC
              name="sendDate"
              label={t('apiMessage.sendDateTime')}
              dateFormat="Pp"
              timeInputLabel={t('apiMessage.timeLabel')}
              showTimeInput
              minDate={new Date()}
              className="tw-w-full"
            />
          )}

          {selectedTemplate
          && selectedTemplate.templateType !== 'text'
          && selectedTemplate.templateType !== 'carousel' && (
            <DropzoneFileUploader
              name="attachedFile"
              maxFiles={1}
              placeholder={t('apiMessage.dragFileText')}
              maxSize={fileConfig.maxSize}
              accept={fileConfig.accept}
            />
          )}
          {selectedTemplate
          && selectedTemplate.templateType === 'carousel'
          && (
            <TemplateTypeCarousel
              carouselData={carouselData}
              fileConfig={fileConfig}
            />
          )}

          {selectedTemplate && (
            <TemplateMessagePreview
              selectedTemplate={selectedTemplate}
              dynamicFieldNames={dynamicFieldNames}
              carouselData={carouselData}
            />
          )}
        </div>
      </FormProviderMC>
    </div>
  );
});

/**
 * Nombre de display para el componente ApiTemplateMessage.
 * @type {string}
 */
ApiTemplateMessage.displayName = 'ApiTemplateMessage';

export default ApiTemplateMessage;
